/**
 * React wrapper for Bryntum SchedulerDatePicker
 */
import React, { RefObject } from 'react';
import { AlignSpec, Base, Container, ContainerItemConfig, ContainerLayoutConfig, DatePicker, DomConfig, Layout, MaskConfig, MenuItemConfig, Model, Month, MonthConfig, Panel, PanelCollapserConfig, PanelCollapserOverlayConfig, PanelHeader, Rectangle, SchedulerDatePicker, SchedulerEventModel, SchedulerEventStore, Scroller, ScrollerConfig, StateProvider, TabConfig, Tool, ToolConfig, ToolbarConfig, TooltipConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumSchedulerDatePickerProps =  {
     // Configs only
    activeDate?              : Date
    adopt?                   : HTMLElement|string
    align?                   : AlignSpec|string
    anchor?                  : boolean
    ariaDescription?         : string
    ariaLabel?               : string
    autoUpdateRecord?        : boolean
    bbar?                    : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    bodyCls?                 : string|object
    bubbleEvents?            : object
    callOnFunctions?         : boolean
    cellRenderer?            : ((renderData: { cell: HTMLElement, date: Date, day: number, rowIndex: number[], row: HTMLElement, cellIndex: number[], columnIndex: number[], visibleColumnIndex: number[] }) => string|DomConfig|void)|string
    centered?                : boolean
    collapsed?               : boolean
    collapsible?             : boolean|Partial<PanelCollapserConfig>|Partial<PanelCollapserOverlayConfig>
    config?                  : object
    constrainTo?             : HTMLElement|Widget|Rectangle
    contentElementCls?       : string|object
    dayNameFormat?           : string
    defaultBindProperty?     : string
    defaultFocus?            : (widget: Widget) => boolean
    defaults?                : object
    disabledDates?           : ((date: Date) => boolean)|Date[]|string
    disableOtherMonthCells?  : boolean
    disableWeekends?         : boolean
    dock?                    : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?               : boolean|{
        handleSelector?: string
    }
    editMonth?               : boolean
    eventFilter?             : ((event: SchedulerEventModel) => boolean)|string
    eventStore?              : SchedulerEventStore
    floating?                : boolean
    focusDisabledDates?      : boolean
    footer?                  : {
        dock?: 'top'|'right'|'bottom'|'left'|'start'|'end'
        html?: string
        cls?: string
    }|string
    header?                  : string|boolean|PanelHeader
    headerRenderer?          : ((renderData: { cell: HTMLElement, day: number, weekDay: number }) => string|DomConfig|void)|string
    hideAnimation?           : boolean|object
    hideOtherMonthCells?     : boolean
    hideWhenEmpty?           : boolean
    htmlCls?                 : string|object
    ignoreParentReadOnly?    : boolean
    itemCls?                 : string
    lazyItems?               : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?               : object
    localeClass?             : typeof Base
    localizable?             : boolean
    localizableProperties?   : string[]
    maskDefaults?            : Partial<MaskConfig>
    masked?                  : boolean|string|Partial<MaskConfig>
    maxDate?                 : Date
    minColumnWidth?          : number
    minDate?                 : Date
    minRowHeight?            : number|string
    monitorResize?           : boolean
    month?                   : Month|Partial<MonthConfig>
    multiSelect?             : boolean|'range'
    namedItems?              : Record<string, Partial<ContainerItemConfig>>
    nonWorkingDays?          : Record<number, boolean>
    owner?                   : Widget
    positioned?              : boolean
    preventTooltipOnTouch?   : boolean
    relayStoreEvents?        : boolean
    ripple?                  : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?             : ShadowRoot
    scrollAction?            : 'hide'|'realign'|null
    showAnimation?           : boolean|object
    showEvents?              : boolean|'count'
    showTooltipWhenDisabled? : boolean
    showWeekColumn?          : boolean
    showWeekNumber?          : boolean
    sixWeeks?                : boolean
    stateful?                : boolean|object|string[]
    statefulEvents?          : object|string[]
    stateId?                 : string
    stateProvider?           : StateProvider
    strips?                  : Record<string, Partial<ContainerItemConfig>>
    tab?                     : boolean|Partial<TabConfig>
    tag?                     : string
    tbar?                    : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    textAlign?               : 'left'|'center'|'right'|'start'|'end'
    textContent?             : boolean
    tip?                     : Partial<TooltipConfig>
    trapFocus?               : boolean
    ui?                      : string|object
    weekRenderer?            : ((renderData: { cell: HTMLElement, week: number[] }) => string|DomConfig|void)|string
    weekStartDay?            : number
    weight?                  : number

     // Configs and properties
    alignSelf?           : string
    animateTimeShift?    : boolean
    appendTo?            : HTMLElement|string
    cls?                 : string|object
    content?             : string
    dataset?             : object
    date?                : Date
    disabled?            : boolean
    extraData?           : string|number|boolean|object
    flex?                : number|string
    height?              : number|string
    hidden?              : boolean
    html?                : string|((widget: Widget) => string)
    id?                  : string
    insertBefore?        : HTMLElement|string
    insertFirst?         : HTMLElement|string
    items?               : (Partial<ContainerItemConfig>|Partial<MenuItemConfig>|Widget)[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>>|Widget[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>|boolean|null>
    layout?              : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?         : object
    margin?              : number|string
    maxHeight?           : string|number
    maximizeOnMobile?    : number|string
    maxWidth?            : string|number
    minHeight?           : string|number
    minWidth?            : string|number
    readOnly?            : boolean
    record?              : Model
    scrollable?          : Scroller|boolean|Partial<ScrollerConfig>
    selection?           : Date[]
    strictRecordMapping? : boolean
    title?               : string
    tools?               : Record<string, Tool|Partial<ToolConfig>>|Record<string, Tool>|Record<string, Partial<ToolConfig>>
    tooltip?             : string|Partial<TooltipConfig>
    width?               : number|string
    x?                   : number
    y?                   : number

     // Properties only
    anchorSize?      : number[]
    isSettingValues? : boolean
    isValid?         : boolean
    state?           : object
    type?            : string
    values?          : Record<string, object>

     // Events
    onBeforeDestroy?   : ((event: { source: Base }) => void)|string
    onBeforeHide?      : ((event: { source: Widget }) => boolean|void)|string
    onBeforeRefresh?   : ((event: { source: DatePicker }) => void)|string
    onBeforeSetRecord? : ((event: { source: Container, record: Model }) => void)|string
    onBeforeShow?      : ((event: { source: Widget|any }) => Promise<boolean|void>)|string
    onCatchAll?        : ((event: {[key: string]: any, type: string}) => void)|string
    onCollapse?        : ((event: { source: Panel }) => void)|string
    onDateChange?      : ((event: { value: Date, oldValue: Date, changes: { d: boolean, w: boolean, m: boolean, y: boolean } }) => void)|string
    onDestroy?         : ((event: { source: Base }) => void)|string
    onExpand?          : ((event: { source: Panel }) => void)|string
    onFocusIn?         : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?        : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onHide?            : ((event: { source: Widget }) => void)|string
    onPaint?           : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onReadOnly?        : ((event: { readOnly: boolean }) => void)|string
    onRecompose?       : (() => void)|string
    onRefresh?         : (() => void)|string
    onResize?          : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onSelectionChange? : ((event: { selection: Date[], userAction: boolean }) => void)|string
    onShow?            : ((event: { source: Widget }) => void)|string
    onToolClick?       : ((event: { source: Tool, tool: Tool }) => void)|string

}

export class BryntumSchedulerDatePicker extends React.Component<BryntumSchedulerDatePickerProps> {

    static instanceClass = SchedulerDatePicker;

    static instanceName = 'SchedulerDatePicker';

    processWidgetContent = processWidgetContent;

    static configNames = [
        'activeDate',
        'adopt',
        'align',
        'anchor',
        'ariaDescription',
        'ariaLabel',
        'autoUpdateRecord',
        'bbar',
        'bodyCls',
        'bubbleEvents',
        'callOnFunctions',
        'cellRenderer',
        'centered',
        'collapsed',
        'collapsible',
        'config',
        'constrainTo',
        'contentElementCls',
        'dayNameFormat',
        'defaultBindProperty',
        'defaultFocus',
        'defaults',
        'disabledDates',
        'disableOtherMonthCells',
        'disableWeekends',
        'dock',
        'draggable',
        'editMonth',
        'eventFilter',
        'eventStore',
        'floating',
        'focusDisabledDates',
        'footer',
        'header',
        'headerRenderer',
        'hideAnimation',
        'hideOtherMonthCells',
        'hideWhenEmpty',
        'htmlCls',
        'ignoreParentReadOnly',
        'itemCls',
        'lazyItems',
        'listeners',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'maxDate',
        'minColumnWidth',
        'minDate',
        'minRowHeight',
        'monitorResize',
        'month',
        'multiSelect',
        'namedItems',
        'nonWorkingDays',
        'owner',
        'positioned',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'ripple',
        'rootElement',
        'scrollAction',
        'showAnimation',
        'showEvents',
        'showTooltipWhenDisabled',
        'showWeekColumn',
        'showWeekNumber',
        'sixWeeks',
        'stateful',
        'statefulEvents',
        'stateId',
        'stateProvider',
        'strips',
        'tab',
        'tag',
        'tbar',
        'textAlign',
        'textContent',
        'tip',
        'trapFocus',
        'ui',
        'weekRenderer',
        'weekStartDay',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'animateTimeShift',
        'appendTo',
        'cls',
        'content',
        'dataset',
        'date',
        'disabled',
        'extraData',
        'flex',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'layout',
        'layoutStyle',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'minHeight',
        'minWidth',
        'onBeforeDestroy',
        'onBeforeHide',
        'onBeforeRefresh',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onCatchAll',
        'onCollapse',
        'onDateChange',
        'onDestroy',
        'onExpand',
        'onFocusIn',
        'onFocusOut',
        'onHide',
        'onPaint',
        'onReadOnly',
        'onRecompose',
        'onRefresh',
        'onResize',
        'onSelectionChange',
        'onShow',
        'onToolClick',
        'readOnly',
        'record',
        'scrollable',
        'selection',
        'strictRecordMapping',
        'title',
        'tools',
        'tooltip',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'isSettingValues',
        'isValid',
        'state',
        'type',
        'values'
    ];

    // Component instance
    instance!: SchedulerDatePicker;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumSchedulerDatePickerProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-scheduler-date-picker-container`;
        return (
            <div className={className} ref={(element) => (this.element = element!)}></div>
        );

    }
}
